/* eslint-disable max-len */
import { createAsyncThunk } from '@reduxjs/toolkit';
import { IExpedient } from 'bg-server/src/components/api/v1/expedient/types';
import { ITramitPopulated } from 'bg-server/src/components/api/v1/tramit/types';
import Translator from 'i18n-babel';

import { toastCall } from 'src/helpers/errorHelpers';
import { IUpdateAttachmentRequest } from 'src/interfaces';
import { attachmentService } from 'src/services/attachment';
import { tramitService } from 'src/services/tramit';

import { setLastActionStatus, setLoadingStatus } from '../app';
import { ICoreState } from '../types';

export const setSelectedTramit = createAsyncThunk<ITramitPopulated, string, { state: ICoreState }>('tramit/setSelectedTramit', async (id, thunkAPI) => {
    const { getState, dispatch } = thunkAPI;
    try {
        const { isLoggedIn } = getState().user;
        if (!isLoggedIn) {
            return null;
        }
        dispatch(setLoadingStatus(true));
        const tramit = await tramitService.getTramitPopulatedById(id);
        dispatch(setLastActionStatus('tramitSelected'));
        dispatch(setLoadingStatus(false));
        return tramit;
    } catch (error) {
        dispatch(setLoadingStatus(error.message ? error.message : error));
        dispatch(setLastActionStatus('tramitSelectError'));
        throw error;
    }
});

export const uploadTramitAttachmentByDocNumber = createAsyncThunk<{ selectedTramit: ITramitPopulated, selectedExpedient: IExpedient }, { tramitId:string, file: File, attachmentType: string }, { state: ICoreState }>('tramit/uploadAttachmentByDocNumber', async (data, thunkAPI) => {
    const { getState, dispatch } = thunkAPI;
    try {
        const { isLoggedIn } = getState().user;
        if (!isLoggedIn) {
            return null;
        }
        const { file, attachmentType, tramitId } = data;
        const response = await attachmentService.uploadTramitAttachmentByDocNumber(file, tramitId, attachmentType);

        const result = {
            selectedTramit: response.tramit,
            selectedExpedient: response.expedient,
        };
        await toastCall(await Translator.t('Document pujat amb èxit'));
        return result;
    } catch (error) {
        dispatch(setLoadingStatus(error.message ? error.message : error));
        throw error;
    }
});

export const uploadTramitAttachmentById = createAsyncThunk<{ selectedTramit: ITramitPopulated, selectedExpedient: IExpedient }, { tramitId:string, file: File, attachmentId: string }, { state: ICoreState }>('tramit/uploadAttachmentById', async (data, thunkAPI) => {
    const { getState, dispatch } = thunkAPI;
    try {
        const { isLoggedIn } = getState().user;
        if (!isLoggedIn) {
            return null;
        }
        const { file, attachmentId, tramitId } = data;
        const response = await attachmentService.uploadTramitAttachmentById(file, tramitId, attachmentId);

        const result = {
            selectedTramit: response.tramit,
            selectedExpedient: response.expedient,
        };
        await toastCall(await Translator.t('Document pujat amb èxit'));
        return result;
    } catch (error) {
        dispatch(setLoadingStatus(error.message ? error.message : error));
        throw error;
    }
});

export const manualSignTramitAttachment = createAsyncThunk<{ selectedTramit: ITramitPopulated, selectedExpedient: IExpedient }, { tramitId:string, file: File, attachmentId: string }, { state: ICoreState }>('tramit/manualSignAttachment', async (data, thunkAPI) => {
    const { getState, dispatch } = thunkAPI;
    try {
        const { isLoggedIn } = getState().user;
        if (!isLoggedIn) {
            return null;
        }
        const { file, attachmentId, tramitId } = data;
        const response = await attachmentService.manualSignTramitAttachment(file, tramitId, attachmentId);

        const result = {
            selectedTramit: response.tramit,
            selectedExpedient: response.expedient,
        };
        await toastCall(await Translator.t('Document pujat amb èxit'));
        return result;
    } catch (error) {
        dispatch(setLoadingStatus(error.message ? error.message : error));
        throw error;
    }
});

export const manualSignTramitAttachmentByAjuntament = createAsyncThunk<{ selectedTramit: ITramitPopulated, selectedExpedient: IExpedient }, { tramitId:string, file: File, attachmentId: string }, { state: ICoreState }>('tramit/manualSignAttachmentByAjuntament', async (data, thunkAPI) => {
    const { getState, dispatch } = thunkAPI;
    try {
        const { isLoggedIn } = getState().user;
        if (!isLoggedIn) {
            return null;
        }
        const { file, attachmentId, tramitId } = data;
        const response = await attachmentService.manualSignTramitAttachmentByAjuntament(file, tramitId, attachmentId);

        const result = {
            selectedTramit: response.tramit,
            selectedExpedient: response.expedient,
        };
        await toastCall(await Translator.t('Document pujat amb èxit'));
        return result;
    } catch (error) {
        dispatch(setLoadingStatus(error.message ? error.message : error));
        throw error;
    }
});

// eslint-disable-next-line max-len
export const deleteTramitAttachment = createAsyncThunk<{ selectedTramit: ITramitPopulated, selectedExpedient: IExpedient }, { tramitId:string, attachmentId: string }, { state: ICoreState }>('tramit/deleteAttachment', async (data, thunkAPI) => {
    const { getState, dispatch } = thunkAPI;
    try {
        const { isLoggedIn } = getState().user;
        if (!isLoggedIn) {
            return null;
        }
        const { attachmentId, tramitId } = data;
        const response = await attachmentService.deleteTramitAttachment(tramitId, attachmentId);
        const result = {
            selectedTramit: response.tramit,
            selectedExpedient: response.expedient,
        };
        return result;
    } catch (error) {
        dispatch(setLoadingStatus(error.message ? error.message : error));
        throw error;
    }
});

/** Permite eleiminar el attachment por completo si su isForceDeletable=true */
export const deleteForceDeletableTramitAttachment = createAsyncThunk<{ selectedTramit: ITramitPopulated, selectedExpedient: IExpedient }, { tramitId:string, attachmentId: string }, { state: ICoreState }>('tramit/deleteForceDeletableAttachment', async (data, thunkAPI) => {
    const { getState, dispatch } = thunkAPI;
    try {
        const { isLoggedIn } = getState().user;
        if (!isLoggedIn) {
            return null;
        }
        const { attachmentId, tramitId } = data;
        const response = await attachmentService.forceDeleteTramitAttachment(tramitId, attachmentId);
        const result = {
            selectedTramit: response.tramit,
            selectedExpedient: response.expedient,
        };
        return result;
    } catch (error) {
        dispatch(setLoadingStatus(error.message ? error.message : error));
        throw error;
    }
});

export const updateTramitAttachment = createAsyncThunk<{ selectedTramit: ITramitPopulated, selectedExpedient: IExpedient }, { tramitId:string, attachmentId: string, body: IUpdateAttachmentRequest }, { state: ICoreState }>('tramit/updateAttachment', async (data, thunkAPI) => {
    const { getState, dispatch } = thunkAPI;
    try {
        const { isLoggedIn } = getState().user;
        if (!isLoggedIn) {
            return null;
        }
        const { attachmentId, tramitId, body } = data;
        const response = await attachmentService.updateTramitAttachment(tramitId, attachmentId, body);
        const result = {
            selectedTramit: response.tramit,
            selectedExpedient: response.expedient,
        };
        dispatch(setLastActionStatus('tramitAttachmentUpdated'));
        return result;
    } catch (error) {
        dispatch(setLoadingStatus(error.message ? error.message : error));
        throw error;
    }
});

export const addTramitDocType = createAsyncThunk<{ selectedTramit: ITramitPopulated, selectedExpedient: IExpedient }, { tramitId:string, docNumber: string }, { state: ICoreState }>('tramit/addDocType', async (data, thunkAPI) => {
    const { getState, dispatch } = thunkAPI;
    try {
        const { isLoggedIn } = getState().user;
        if (!isLoggedIn) {
            return null;
        }
        const { docNumber, tramitId } = data;
        const response = await tramitService.addTramitDocType(tramitId, docNumber);
        const result = {
            selectedTramit: response.tramit,
            selectedExpedient: response.expedient,
        };
        dispatch(setLastActionStatus('tramitDocTypeAdded'));
        return result;
    } catch (error) {
        dispatch(setLoadingStatus(error.message ? error.message : error));
        throw error;
    }
});

export const updateTramit = createAsyncThunk<ITramitPopulated, { tramitId:string, form: any }, { state: ICoreState }>('tramit/updateTramit', async (data, thunkAPI) => {
    const { getState, dispatch } = thunkAPI;
    try {
        const { isLoggedIn } = getState().user;
        if (!isLoggedIn) {
            return null;
        }
        const { form, tramitId } = data;
        const updatedTramit = await tramitService.updateTramit(tramitId, form);
        dispatch(setLastActionStatus('tramitUpdated'));
        return updatedTramit;
    } catch (error) {
        dispatch(setLoadingStatus(error.message ? error.message : error));
        throw error;
    }
});
